import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { IModifier } from '@Core/Events/EventModifiers/Modifier';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { TextContainer } from '@Core/TextContainer/TextContainer';
import { AnimationStateListener, Event, Physics, Spine, TrackEntry, Vector2 } from '@esotericsoftware/spine-pixi-v8';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { TPoint } from '@GFTypes/Point';
import { ISpineConfig } from '@GFTypes/Spines';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { hasTextVariableInLanguageFile } from '@Tools/TextVariablesUtils';
import i18next from 'i18next';
import { Assets, Container, Point } from 'pixi.js';
import { GameScene } from '..';
import SpineWrapperEventManager from './SpineWrapperEventManager';
import SpineWrapperSlotManager from './SpineWrapperSlotManager';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';

export enum ESpineEventParameters {
    FIT_TO_SCREEN = 'fitToScreen',
    FULL_SCREEN = 'fullScreen',
    ATTACH_TO_REELS = 'attachToReels',
    SCALE_TO_REELS_WIDTH = 'scaleToReelsWidth',
    Z_INDEX = 'zIndex',
    LOOP_FROM = 'loopFrom',
    CHANGE_REEL_COUNT = 'changeReelCount'
}

export interface ICancelablePromise {
    promise: Promise<TrackEntry | void>;
    cancel: () => void;
}

export default class SpineWrapper extends Container {
    protected static readonly ANIMATION_OBJECT_TYPE: 'spine';
    protected static readonly COMPLETE: string = 'complete';
    protected static readonly EMPTY_ANIMATION: string = 'empty';
    private readonly gameStore: GameStore;

    public dataKey: string;
    protected atlasKey: string;
    protected skinName: string;
    public scene: GameScene;
    public spine: Spine;
    public fitToScreen: boolean = false;
    public fullScreen: boolean = false;
    public attachToReels: boolean = false;
    public scaleToReelsWidth: boolean = false;
    public spineZIndex: number | undefined;
    public hasEmptyZIndexEvent: boolean = false;
    public loopFrom: number | undefined;
    public slotManager: SpineWrapperSlotManager;
    public eventManager: SpineWrapperEventManager;
    public turboAffectsAnimationSpeed: boolean;
    public delayFunction: (() => number) | undefined;
    public texts: TextContainer[] = [];
    public textsWithVariables: Map<string, TextContainer> = new Map();

    private oncePerLoopFrom: boolean = false;

    constructor(
        scene: GameScene,
        position: TPoint,
        config: ISpineConfig,
        subscribeForEvents?: boolean,
        turboAffectsAnimationSpeed: boolean = true,
        localEventModifiers: IModifier[] = []
    ) {
        Logger.log(LoggerLevel.Constructor, {
            message: 'SpineWrapper constructor',
            source: LoggerSource.Game
        });
        super();
        this.scene = scene;
        this.gameStore = scene.game.gameStore;
        this.dataKey = config.dataKey;
        this.atlasKey = config.atlasKey ?? AssetsLoadManager.getAtlasBySpineKey(this.dataKey);
        this.spine = new Spine({ x: position.x, y: position.y, skeleton: this.dataKey, atlas: this.atlasKey, parent: this.scene.stage, autoUpdate: false });
        this.fitToScreen = !!this.findSpineEvent(ESpineEventParameters.FIT_TO_SCREEN)?.intValue;
        this.fullScreen = !!this.findSpineEvent(ESpineEventParameters.FULL_SCREEN)?.intValue;
        this.scaleToReelsWidth = !!this.findSpineEvent(ESpineEventParameters.SCALE_TO_REELS_WIDTH)?.intValue;
        this.attachToReels = !!this.findSpineEvent(ESpineEventParameters.ATTACH_TO_REELS)?.intValue;
        this.spineZIndex = this.findSpineEvent(ESpineEventParameters.Z_INDEX)?.intValue;
        this.hasEmptyZIndexEvent =
            !!this.findRawSpineEvent(ESpineEventParameters.Z_INDEX) && this.findRawSpineEvent(ESpineEventParameters.Z_INDEX).int === undefined;
        this.loopFrom = this.findSpineEvent(ESpineEventParameters.LOOP_FROM)?.intValue;
        this.setDefaultSkin(config);
        this.slotManager = new SpineWrapperSlotManager(scene, this);
        this.eventManager = new SpineWrapperEventManager(scene, this, !!subscribeForEvents, localEventModifiers);
        this.turboAffectsAnimationSpeed = turboAffectsAnimationSpeed;
        this.spine.state.data.defaultMix = 0.01;

        if (this.spineZIndex && !this.hasEmptyZIndexEvent) this.spine.zIndex = this.spineZIndex;
        if (this.attachToReels) this.updateAttachToReels(this.attachToReels);
        if (this.scaleToReelsWidth) this.updateScaleToReelsWidth(this.scaleToReelsWidth);
        if (!config.manuallyScaleOnResize) this.scene.game.scaleManager.addSpineToScalableMap(this);

        this.spine.state.addListener({
            event: (_, event) => {
                if (event.data.name === ESpineEventParameters.FIT_TO_SCREEN) this.updateFitToScreen(!!event.intValue);
                if (event.data.name === ESpineEventParameters.FULL_SCREEN) this.updateFullScreen(!!event.intValue);
                if (event.data.name === ESpineEventParameters.Z_INDEX) this.updateZIndex(event.intValue);
                if (event.data.name === ESpineEventParameters.ATTACH_TO_REELS) this.updateAttachToReels(!!event.intValue);
                if (event.data.name === ESpineEventParameters.SCALE_TO_REELS_WIDTH) this.updateScaleToReelsWidth(!!event.intValue);
                if (event.data.name === ESpineEventParameters.LOOP_FROM) this.updateLoopFrom(event.intValue);
                if (event.data.name === ESpineEventParameters.CHANGE_REEL_COUNT) this.emitChangeReelCount();
            }
        });

        const translatableSlots = this.slotManager.getAllTranslatableSlotNames();
        if (translatableSlots.length) {
            translatableSlots.forEach((slot) => {
                const textContainer = TextContainer.fromSlot(slot, scene);
                this.texts.push(textContainer);
                this.slotManager.assignObjectToSlot(slot, textContainer);
                if (hasTextVariableInLanguageFile(slot)) this.textsWithVariables.set(slot, textContainer);
            });

            if (this.textsWithVariables.size > 0) {
                this.gameStore.on(EGameStoreKeys.TextVariables, this.updateVariablesInTexts, this);
            }
        }

        this.scene.game.spines.push(this);
        this.scene.stage.addChild(this.spine);
    }

    private updateVariablesInTexts(): void {
        const textVariables = { ...this.gameStore.get(EGameStoreKeys.TextVariables) };
        this.textsWithVariables.entries().forEach(([slot, textContainer]) => {
            textContainer.setText(i18next.t(slot, { ...textVariables }));
        });
    }

    private setDefaultSkin(config: ISpineConfig): void {
        if (!config.skinName || config.skinName === 'default') {
            const skinWithTranslation = this.spine.skeleton.data.skins.find(
                (skin) => skin.name.includes(`_${ConfigLoader.language}`) || skin.name === ConfigLoader.language
            );
            this.spine.skeleton.setSkin(skinWithTranslation ? skinWithTranslation.name : 'default');
        } else {
            this.spine.skeleton.setSkin(config.skinName);
        }
    }

    public setPosition(x?: number, y?: number): void {
        this.spine.position = new Point(x, y);
    }

    public setScale(x: number, y?: number): void {
        this.spine.scale = new Point(x, y);
    }

    private updateFitToScreen(fitToScreen: boolean): void {
        if (this.fitToScreen === fitToScreen) return;
        this.fitToScreen = fitToScreen;
        this.scene.game.scaleManager.applyScaleToElement(this, this.fitToScreen, this.fullScreen);
    }

    private updateFullScreen(fullScreen: boolean): void {
        if (this.fullScreen === fullScreen) return;
        this.fullScreen = fullScreen;
        this.scene.game.scaleManager.applyScaleToElement(this, this.fitToScreen, this.fullScreen);
    }

    private updateScaleToReelsWidth(scaleToReelsWidth: boolean): void {
        this.scaleToReelsWidth = scaleToReelsWidth;
        this.scene.game.scaleManager.applyScaleToElement(this, this.fitToScreen, this.fullScreen);
    }

    private updateZIndex(spineZIndex: number): void {
        if (spineZIndex === undefined || this.hasEmptyZIndexEvent) return;
        this.spineZIndex = spineZIndex;
        this.spine.zIndex = this.spineZIndex;
    }

    private updateAttachToReels(attachToReels: boolean): void {
        this.attachToReels = attachToReels;
        this.attachToReels ? this.scene.game.scaleManager.attachSpineToReels(this) : this.scene.game.scaleManager.detachSpineFromReels(this);
    }

    private updateLoopFrom(loopFrom: number): void {
        this.loopFrom = loopFrom;

        const currentTrack = this.getCurrentAnimationTrack();

        if (!currentTrack) return;

        const fps = this.spine.skeleton.data.fps ?? 30;
        const sigleFrameDuration = 1 / fps;
        if (!this.oncePerLoopFrom) {
            currentTrack.loop = false;
            const track = this.spine.state.addAnimation(0, currentTrack?.animation?.name!, true, 0);
            track.animationStart = sigleFrameDuration * this.loopFrom;
            track.animationLast = track.animationStart;
            this.oncePerLoopFrom = true;
        }
    }

    private emitChangeReelCount(): void {
        EventsCenter.emit(EGameEvents.CHANGE_REEL_COUNT);
    }

    private findRawSpineEvent(name: string): any {
        return Assets.get(this.dataKey).events?.[name];
    }

    private findSpineEvent(name: string): Event | undefined {
        return this.spine.skeleton.data.events.find((event) => event.name === name)?.setupPose;
    }

    public getCurrentAnimationTrack(trackIndex?: number): TrackEntry | null {
        return this.spine.state.getTrack(trackIndex ?? 0);
    }

    public getCurrentAnimationName(trackIndex?: number): string | undefined {
        return this.getCurrentAnimationTrack(trackIndex)?.animation?.name;
    }

    public play(animationName: string, loop?: boolean, trackIndex?: number, shouldIgnoreBlockers?: boolean): TrackEntry | void {
        if (this.eventManager.isBlocked() && !shouldIgnoreBlockers) return this.getCurrentAnimationTrack()!;
        this.oncePerLoopFrom = false;
        if (this.delayFunction && this.delayFunction() > 0) {
            //TODO: Migrate to PIXI
            // this.scene.time.delayedCall(this.delayFunction(), () => {
            //     this.spine.state.setAnimation(trackIndex ?? 0, animationName, loop);
            // });
        } else {
            const trackEntry = this.spine.state.setAnimation(trackIndex ?? 0, animationName, loop);
            return trackEntry;
        }
    }

    public onTrackComplete(track: TrackEntry, callback: () => any): void {
        track.listener = {
            complete: callback
        };
    }

    public waitForTrackEvent(track: TrackEntry, eventName: keyof AnimationStateListener, trackIndex: number = 0): ICancelablePromise {
        let listener: AnimationStateListener;
        const promise: Promise<TrackEntry> = new Promise((resolve, reject) => {
            listener = {
                [eventName]: (entry: TrackEntry) => {
                    if (entry.trackIndex === trackIndex) {
                        track.listener = null;
                        resolve(entry);
                    }
                }
            };
            track.listener = listener;
        });
        const cancel = () => (track.listener = null);
        return { promise, cancel };
    }

    public waitForAnimationEvent(animationName: string, eventName: keyof AnimationStateListener, ids?: number[]): ICancelablePromise {
        let listener: AnimationStateListener;
        const promise: Promise<TrackEntry> = new Promise((resolve, reject) => {
            listener = {
                [eventName]: (entry: TrackEntry) => {
                    if (entry.animation?.name === this.eventManager.getAnimationNameForGameEvent(animationName, ids)) {
                        setTimeout(() => this.spine.state.removeListener(listener), 0);
                        resolve(entry);
                    }
                }
            };
            this.spine.state.addListener(listener);
        });
        const cancel = () => this.spine.state.removeListener(listener);
        return { promise, cancel };
    }

    public stop(mixDuration: number = 0.25): void {
        this.spine.state.setEmptyAnimation(0, mixDuration);
    }

    public destroy(): void {
        this.spine.destroy(true);
    }

    public addToContainer(container: Container): void {
        container.addChild(this.spine);
    }

    public setSpeed(modifier: number): void {
        this.spine.state.timeScale = modifier;
    }

    public getAnimationDuration(animationName: string, trackIndex: number = 0, useTimeScale: boolean = true): number {
        const animation = this.spine.skeleton.data.findAnimation(animationName);

        if (!animation) {
            console.warn(`SpineWrapper: Animation '${animationName}' not found.`);
            return 0;
        }

        let duration = animation.duration * 1000;

        if (useTimeScale) {
            const track = this.getCurrentAnimationTrack(trackIndex);
            if (track) duration /= track.timeScale;
            else duration /= this.spine.state.timeScale;
        }

        return duration;
    }

    public assignDelayFunction(delayFunction: () => number): void {
        this.delayFunction = delayFunction;
    }

    public forceUpdate(): void {
        this.spine.state.update(0);
        this.spine.state.apply(this.spine.skeleton);
        this.spine.skeleton.updateWorldTransform(Physics.update);
    }
}
